import { APP_BASE_HREF } from "@angular/common";
import { Injectable, NgZone, inject } from "@angular/core";
import { NoiseSuppressionEffect } from "@auvious/media-tools";
import { IPublishedStream, IStream, StreamTypes } from "@auvious/rtc";
import { Subject } from "rxjs";

import { StreamTrackKindEnum } from "../core-ui.enums";
import { IStreamMetadata } from "../models";
import { CustomerParam } from "../models/application-options/CustomerOptions";
import { AppConfigService } from "./app.config.service";
import { ConferenceService } from "./conference.service";
import { DeviceService } from "./device.service";
import { GenericErrorHandler } from "./error-handlers.service";
import { LocalMediaService } from "./local.media.service";
import { UserService } from "./user.service";

@Injectable()
export class NoiseSuppressionService {
  private local = inject(LocalMediaService);
  private errorHandler = inject(GenericErrorHandler);
  private zone = inject(NgZone);
  private baseHref = inject(APP_BASE_HREF);
  private user = inject(UserService);
  private config = inject(AppConfigService);
  private conference = inject(ConferenceService);

  private readonly moduleUrl = new URL(
    "assets/noise-suppression/audio-worklet-processor.js",
    new URL(this.baseHref, location.origin)
  ).href;

  private effect: NoiseSuppressionEffect | null = null;
  private streamId: string | null = null;

  // When an agent disables the customer's suppression, keep it off for the rest
  // of the call: block the auto re-enable paths (unmute / stream restart) until
  // an agent explicitly re-enables it.
  private _disabledByAgent = false;

  // When an agent turns suppression on (e.g. it is not on by default for the
  // customer), keep it on across mute/unmute until the agent turns it off.
  private _enabledByAgent = false;

  // An operator's (agent/supervisor/admin) own choice to run suppression on
  // their outgoing mic. Local-only, never published. Sticky across mute/unmute
  // and device changes; starts off and resets each call (service is call-scoped).
  private _enabledBySelf = false;

  private _noiseSuppressionStreamState = new Subject<{
    id: string;
    enabled: boolean;
  }>();
  public noiseSuppressionStreamState$ =
    this._noiseSuppressionStreamState.asObservable();

  constructor() {
    // The customer publishes its suppression state on the stream metadata so
    // the agent (incl. late joiners) can reflect it in the tile controls.
    this._noiseSuppressionStreamState.subscribe(({ id, enabled }) =>
      this.publishState(this.findOwnPublishedStream(id), enabled)
    );
    this.conference.localStreamPublished$.subscribe((stream) => {
      if (stream) {
        this.publishState(stream, this.isEnabled(stream));
      }
    });

    this.local.localStreamReady$.subscribe((stream) => {
      if (
        stream.type === StreamTypes.MIC ||
        stream.type === StreamTypes.VIDEO
      ) {
        this.enable(stream);
      }
    });

    this.local.streamRemoved$.subscribe((stream) => {
      if (!this.local.hasLiveInput(StreamTrackKindEnum.audio)) {
        this.disable(stream);
      }
    });

    this.local.streamMutedChange$.subscribe((event) => {
      if (event.trackKind !== StreamTrackKindEnum.audio) {
        return;
      }

      if (event.muted) {
        this.disable(event.stream);
      } else if (
        event.stream?.type === StreamTypes.MIC ||
        event.stream?.type === StreamTypes.VIDEO
      ) {
        this.enable(event.stream);
      }
    });
  }

  public enable(stream: IStream) {
    // Not supported on mobile devices (phones and tablets), regardless of the
    // settings or an agent request. The browser's native noise suppression
    // (getUserMedia constraint) still applies.
    if (DeviceService.isMobile) {
      return;
    }

    if (this.user.isCustomer) {
      // Allowed either when it is on by default for the customer, or when an
      // agent has turned it on (supports the flow where suppression is off by
      // default but agent-controllable). The agent-control feature flag lives in
      // the agent config, which the customer cannot read, so it is enforced on
      // the agent side (tile-control visibility) and by verifying the command
      // sender is an agent.
      const allowedByDefault = this.config.customerParamEnabled(
        CustomerParam.NOISE_SUPPRESSION_ENABLED
      );
      const allowedByAgent = this._enabledByAgent;

      if (!allowedByDefault && !allowedByAgent) {
        return;
      }

      // An agent has disabled it for this call; do not auto re-enable.
      if (this._disabledByAgent) {
        return;
      }
    } else {
      // Operator (agent/supervisor/admin) applying suppression to their own
      // outgoing mic. Gated solely by the live toggle, which can only be set
      // through the (config-gated) stream-controls button.
      if (!this._enabledBySelf) {
        return;
      }
    }

    const audioTrack = stream.mediaStream?.getAudioTracks()[0];

    if (!audioTrack || audioTrack.readyState !== "live") {
      return;
    }

    if (this.effect) {
      this.streamId = stream.id;
      this._noiseSuppressionStreamState.next({
        id: stream.id,
        enabled: true
      });
      return;
    }

    const effect = new NoiseSuppressionEffect({
      moduleUrl: this.moduleUrl,
      fallbackToInputOnError: true,
      bypassUntilReady: true
    });

    effect.events.on("fail", (error) => {
      this.errorHandler.handleError(error);
      this.disable(stream);
    });
    effect.events.on("ready", () => {
      if (this.streamId) {
        this._noiseSuppressionStreamState.next({
          id: this.streamId,
          enabled: true
        });
      }
    });

    this.effect = effect;
    this.streamId = stream.id;
    this.zone.runOutsideAngular(() => this.local.pipe.addEffect(effect));
  }

  public disable(stream?: IStream) {
    if (!this.effect) {
      return;
    }

    const streamId = stream?.id || this.streamId;
    const effect = this.effect;
    this.effect = null;
    this.streamId = null;

    this.zone.runOutsideAngular(() => this.local.pipe.removeEffect(effect));
    effect.dispose();

    if (streamId) {
      this._noiseSuppressionStreamState.next({ id: streamId, enabled: false });
    }
  }

  public isEnabled(stream: IStream) {
    return (
      !!this.effect && this.streamId === stream?.getMetadata()?.correlationId
    );
  }

  /**
   * Apply an agent-initiated change to the customer's noise suppression.
   * A disable is sticky (blocks auto re-enable); an enable clears that guard.
   */
  public setByAgent(enabled: boolean) {
    if (enabled) {
      this._disabledByAgent = false;
      this._enabledByAgent = true;
      const stream = this.conference.localStream;
      if (stream) {
        this.enable(stream);
      }
    } else {
      this._enabledByAgent = false;
      this._disabledByAgent = true;
      this.disable();
    }
  }

  /** The operator's own suppression intent (stable across mute/unmute). */
  public get isEnabledForSelf(): boolean {
    return this._enabledBySelf;
  }

  /**
   * Apply an operator-initiated change to noise suppression on their own
   * outgoing mic. Local processing only; no metadata is published. The intent is
   * sticky so the existing lifecycle subscriptions (unmute / device change)
   * auto re-apply it via enable().
   */
  public setForSelf(enabled: boolean) {
    if (this.user.isCustomer) {
      return;
    }
    this._enabledBySelf = enabled;
    const stream = this.local.mainStream?.stream;
    if (enabled) {
      if (stream) {
        this.enable(stream);
      }
    } else {
      this.disable(stream);
    }
  }

  public toggleSelf() {
    this.setForSelf(!this._enabledBySelf);
  }

  /**
   * Resolve the customer's own published stream a suppression change refers to.
   * The tracked id is a published stream `.id` on some paths (unmute, agent
   * control) and a correlationId on others (pre-publish stream-ready), so match
   * on either. Limited to the audio-bearing local streams (main + secondary) so
   * we never publish onto an unrelated sibling stream.
   */
  private findOwnPublishedStream(id: string): IPublishedStream | null {
    return (
      [this.local.mainStream?.stream, this.local.secondaryStream?.stream].find(
        (s) =>
          !!s &&
          (s.id === id ||
            (s.getMetadata() as IStreamMetadata)?.correlationId === id)
      ) ?? null
    );
  }

  /**
   * Publish the current suppression state on the given published stream's
   * metadata so remote participants (the agent) can observe it.
   */
  private publishState(stream: IPublishedStream | null, enabled: boolean) {
    if (!this.user.isCustomer || !stream) {
      return;
    }

    const current = stream.getMetadata() as IStreamMetadata;
    // updateStreamMetadata does a shallow merge, so spread the existing audio
    // metadata to avoid dropping its settings.
    this.conference.updateStreamMetadata(stream, {
      audio: { ...current?.audio, noiseSuppression: enabled }
    });
  }
}
